export type Player = { id: string; name: string; updatedAt?: number }
export type Team = { id: string; name: string; members: { playerId: string; jersey: string }[]; updatedAt?: number }
export type Participant = { id: string; name: string; jersey: string; available: boolean; temporary: boolean }
export type GameEvent = { id: string; sequence: number; at: number; type: 'start' | 'pause' | 'resume' | 'sub' | 'foul' | 'end' | 'undo' | 'availability'; lineup?: string[]; out?: string; in?: string; playerId?: string; delta?: number; target?: string; available?: boolean; participant?: Participant }
export type Game = { id: string; teamId: string; teamName: string; opponent: string; foulLimit: number; participants: Participant[]; events: GameEvent[]; clockWarning?: boolean; updatedAt?: number }

export function projectGame(game: Game, now: number) {
  const undone = new Set(game.events.filter(e => e.type === 'undo').map(e => e.target))
  const participants = [...game.participants]
  for (const e of game.events) if (e.participant && !participants.some(p => p.id === e.participant!.id)) participants.push({ ...e.participant, available: false })
  const available = new Set(game.participants.filter(p => p.available).map(p => p.id))
  let lineup: string[] = [], status: 'running' | 'paused' | 'ended' = 'paused', previous = game.events[0]?.at || now
  const played: Record<string, number> = {}, rested: Record<string, number> = {}, stint: Record<string, number> = {}, fouls: Record<string, number> = {}
  for (const p of participants) { played[p.id] = 0; rested[p.id] = 0; stint[p.id] = 0; fouls[p.id] = 0 }
  const accrue = (until: number) => {
    const elapsed = Math.max(0, until-previous)
    if (status === 'running') for (const p of participants.filter(p => available.has(p.id))) {
      stint[p.id] += elapsed
      if (lineup.includes(p.id)) played[p.id] += elapsed
      else rested[p.id] += elapsed
    }
    previous = Math.max(previous, until)
  }
  for (const e of [...game.events].sort((a,b) => a.sequence-b.sequence)) {
    accrue(e.at)
    if (undone.has(e.id)) continue
    if (e.type === 'start') { lineup = e.lineup || []; status = 'running' }
    if (e.type === 'pause') status = 'paused'
    if (e.type === 'resume') status = 'running'
    if (e.type === 'end') status = 'ended'
    if (e.type === 'availability' && e.playerId) { if (e.available) available.add(e.playerId); else available.delete(e.playerId); stint[e.playerId] = 0 }
    if (e.type === 'sub' && e.out && e.in) { lineup = lineup.map(id => id === e.out ? e.in! : id); stint[e.out] = 0; stint[e.in] = 0 }
    if (e.type === 'foul' && e.playerId) fouls[e.playerId] = Math.max(0, fouls[e.playerId] + (e.delta || 0))
  }
  accrue(now)
  return { lineup, status, played, rested, stint, fouls, participants, available: [...available] }
}

export function appendEvent(game: Game, event: Omit<GameEvent, 'id' | 'sequence' | 'at'>, now = Date.now()): Game {
  const state = projectGame(game, now)
  if (state.status === 'ended') throw new Error('This game has ended.')
  if (event.type === 'sub') {
    if (!event.out || !event.in || !state.lineup.includes(event.out) || state.lineup.includes(event.in) || !state.available.includes(event.in)) throw new Error('Select one on-court player and an available bench replacement.')
  }
  if (event.type === 'pause' && state.status !== 'running') throw new Error('Game is already paused.')
  if (event.type === 'resume' && state.status !== 'paused') throw new Error('Game is already running.')
  if (event.type === 'foul' && !state.available.includes(event.playerId || '')) throw new Error('Player is unavailable.')
  if (event.type === 'availability' && !event.available && state.lineup.includes(event.playerId || '')) throw new Error('Substitute this player off court before marking them unavailable.')
  if (event.type === 'undo') {
    const target = game.events.find(e => e.id === event.target)
    if (!target || !['sub','foul'].includes(target.type) || game.events.some(e => e.type === 'undo' && e.target === event.target)) throw new Error('This action cannot be undone.')
    if (target.type === 'sub' && !state.available.includes(target.out || '')) throw new Error('Mark the outgoing player available before undoing this substitution.')
  }
  const last = game.events.at(-1)
  return { ...game, updatedAt: now, clockWarning: game.clockWarning || now < (last?.at || 0), events: [...game.events, { ...event, id: crypto.randomUUID(), sequence: (last?.sequence || 0)+1, at: Math.max(now, last?.at || 0) }] }
}

export function lastGameAction(game: Game, type?: 'sub' | 'foul'): GameEvent | undefined {
  const undone = new Set(game.events.filter(e => e.type === 'undo').map(e => e.target))
  return [...game.events].sort((a,b) => b.sequence-a.sequence).find(e => (type ? e.type === type : ['sub','foul'].includes(e.type)) && !undone.has(e.id))
}

export function rotationParticipants(state: ReturnType<typeof projectGame>, onCourt: boolean): Participant[] {
  return state.participants.filter(p => state.lineup.includes(p.id) === onCourt && state.available.includes(p.id))
    .sort((a,b) => state.stint[b.id]-state.stint[a.id])
}

export function undoGameAction(game: Game): Game {
  const latest = lastGameAction(game)
  if (!latest) throw new Error('No substitution or foul left to undo.')
  return appendEvent(game, { type: 'undo', target: latest.id })
}
